import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DetailsCardDetailList, {
    formatDate,
    formatStringOrNumber,
    SubjectProfileDetailsCardItem,
} from './details-card-detail-list';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION } from '@queries/subject-monitoring';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';

type Props = {
    studyRef: string;
    datasetId: number;
    subjectId: number;
};

const SubjectProfileDetailsPersonalInformationCard = ({ studyRef, datasetId, subjectId }: Props) => {
    const { t: tCommon } = useTranslation('common');

    const { t } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.personalInformation',
    });

    const getSubjectDetailsQuery = SubjectMonitoringAPI.useGetSubjectDetails(studyRef, datasetId, subjectId);
    const getSubjectProfileDetailsConfigQuery = useQuery(GET_SUBJECT_PROFILE_DETAILS_CONFIGURATION, {
        variables: { studyId: studyRef },
    });

    const subjectDetails = getSubjectDetailsQuery.data;
    const sectionConfig =
        getSubjectProfileDetailsConfigQuery.data?.subjectProfileDetailsConfiguration
            ?.subjectProfileDetailsConfigurationItem?.personalInformation;

    const isLoading = getSubjectDetailsQuery.isLoading || getSubjectProfileDetailsConfigQuery.loading;

    const isError = !!getSubjectDetailsQuery.error || !!getSubjectProfileDetailsConfigQuery.error;

    const items: SubjectProfileDetailsCardItem[] = [];

    if (sectionConfig?.ageAtEnrolment) {
        items.push({
            key: 'ageAtEnrolment',
            label: t('ageAtEnrolment'),
            value: formatStringOrNumber(subjectDetails?.ageAtEnrollment),
        });
    }
    if (sectionConfig?.gender) {
        items.push({
            key: 'gender',
            label: t('gender'),
            value: formatStringOrNumber(subjectDetails?.gender),
        });
    }
    if (sectionConfig?.ethnicity) {
        items.push({
            key: 'ethnicity',
            label: t('ethnicity'),
            value: formatStringOrNumber(subjectDetails?.ethnicity),
        });
    }
    if (sectionConfig?.race) {
        items.push({
            key: 'race',
            label: t('race'),
            value: formatStringOrNumber(subjectDetails?.race),
        });
    }
    if (sectionConfig?.latestConsentDate) {
        items.push({
            key: 'lastConsentDate',
            label: t('lastConsentDate'),
            value: formatDate(subjectDetails?.latestConsentDate),
        });
    }
    if (sectionConfig?.latestConsentVersion) {
        items.push({
            key: 'lastConsentVersion',
            label: t('lastConsentVersion'),
            value: formatStringOrNumber(subjectDetails?.latestConsentVersion),
        });
    }

    if (!isLoading && !isError && items.length === 0) {
        return null;
    }

    return (
        <Card
            data-testid="subject-profile-details-personal-information-card"
            title={sectionConfig?.label ?? 'Information'}
            padding="none"
            collapsible
        >
            <div className={styles.cardIconAndDetailsListWrapper}>
                <div className={styles.cardIconWrapper}>
                    <FontAwesomeIcon icon={['fas', 'user']} className={styles.cardIcon} />
                </div>

                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    <DetailsCardDetailList items={items} />
                )}
            </div>
        </Card>
    );
};

export default SubjectProfileDetailsPersonalInformationCard;
